/*!
	* flight-times v1.0.0
	* (c) 2024 Klonbits, Inc.
	* All rights reserved.
	*/

/**
	* Generates a flight schedule based on the start time, end time, and minute interval.
	* @param {number} startTime - The start time in the format HHMM (e.g., 800 for 08:00).
	* @param {number} endTime - The end time in the format HHMM (e.g., 1800 for 18:00).
	* @param {number} minuteInterval - The interval between flights in minutes.
	* @returns {string[]} The array containing the flight schedule in the format ["HH:MM", "HH:MM", ...].
	* @throws {Error} If the startTime or endTime is in an invalid format.
	*/
function generateFlightSchedule(startTime, endTime, minuteInterval) {
	// Check if the startTime and endTime are valid
	if (!isValidTimeFormat(startTime) || !isValidTimeFormat(endTime)) { throw new Error('Invalid time format. Expected format: HHMM (e.g., 800 for 08:00).'); }
	const [start, end, interval] = [startTime, endTime, minuteInterval].map(value => parseInt(value, 10));

	const schedule = [];
	let hour = Math.floor(start / 100); // Extracts the hour part from the start time
	let minute = start % 100; // Extracts the minute part from the start time

	while (hour * 100 + minute <= end) {
		const hourValue = ("0" + hour).slice(-2); // Converts the hour value to a two-digit number
		const minuteValue = ("0" + minute).slice(-2); // Converts the minute value to a two-digit number
		schedule.push(hourValue + ":" + minuteValue);
		minute += interval;
		if (minute >= 60) { hour++; minute %= 60; }
	}

	return schedule;
}

/**
	* Checks the index of the selected flight time in the given array of flight times.
	* @param {string[]} flightTimes - Array of flight times
	* @param {string} selectedTimeClassName - Class name of the selected time elements
	* @returns {number|null} - Index of the selected time in the array, or null
	*/
function checkSelectedFlightTime(flightTimes, selectedTimeClassName) {
	// Get all elements with the selected time class
	const selectedTimeElements = document.getElementsByClassName(selectedTimeClassName);
	// Get the value of the first selected time element
	const selectedTime = selectedTimeElements[0].value;
	// Check the index of the selected time in the array
	const index = flightTimes.indexOf(selectedTime);
	// Return the index if the selected time is found, or null if it's not found
	return index !== -1 ? index : null;
}

/**
	* Delays the execution of a function by debouncing it.
	* @param {Function} func - The function to be debounced.
	* @param {number} delay - The delay in milliseconds.
	* @returns {Function} - The debounced function.
	*/
function debounceInputListener(func, delay) { let timeoutId; return function (...args) { clearTimeout(timeoutId); timeoutId = setTimeout(() => { func.apply(this, args); }, delay); }; }

/**
		* Default input listener function.
		* @param {string[]} flightTimes - The flight schedule array.
		* @returns {void}
		*/
function defaultInputListener(flightTimes, event) { console.debug("Input detected: ", this.value, event.target.value, flightTimes); };

/**
* Adds an input listener to elements with the "js-flight-times" class.
* @param {function} [inputListener] - Optional input listener callback function.
* @returns {void}
*/
function addInputListenerToFlightTimesSliders(inputListener) {
	const flightTimesSliders = document.getElementsByClassName("js-flight-times");

	// Add the input listener to each element
	for (const flightTimesSlider of flightTimesSliders) {
		let { interval = 5, selectionRange = "0000|2359", selectedTime, delay = 300 } = flightTimesSlider.dataset;
		let [start, end] = selectionRange.split('|');

		let generatedFlightSchedule = generateFlightSchedule(start, end, interval);
		let listener = inputListener || defaultInputListener;

		flightTimesSlider.max = generatedFlightSchedule.length - 1;
		flightTimesSlider.readOnly = true;
		flightTimesSlider.value = checkSelectedFlightTime(generatedFlightSchedule, selectedTime);
		flightTimesSlider.addEventListener("input", debounceInputListener(listener.bind(flightTimesSlider, generatedFlightSchedule), delay));
		flightTimesSlider.dispatchEvent(new Event("input", { bubbles: true }));
	}
}

/**
	* Checks if the given time value has a valid format (HHMM).
	* @param {number|string} time - The time value to check.
	* @returns {boolean} - Indicates whether the time format is valid or not.
	*/
function isValidTimeFormat(time) { const regex = /^(0[0-9]|1[0-9]|2[0-3])[0-5][0-9]$/; return regex.test(time); }

/**
	* Validates the selected value based on a validity range and invokes a callback if provided.
	* @param {string} selectedValue - The selected value to validate
	* @param {string} validityRange - The validity range in the format "start|end"
	* @param {string} [validityCb] - The name of the callback function to invoke (optional)
	* @returns {boolean} - Indicates whether the selected value is valid or not
	*/
const validateSelectedValue = (selectedValue, validityRange, validityCb) => {
	const [start, end] = validityRange.split('|');
	const isValid = selectedValue >= start && selectedValue <= end;
	if (validityCb && typeof window[validityCb] === 'function') { window[validityCb]?.({ valid: isValid }); }
	return isValid;
};

/**
	* Updates other elements with the selected time.
	* @param {string[]} flightTimes - Array of flight times
	* @param {string} selectedTime - Class name of the selected time elements
	* @param {number} selectedIndex - Index of the selected time in the array
	* @returns {void}
	*/
const updateDisplayerElements = (flightTimes, selectedTime, selectedIndex) => {
	// Update other elements with the selected time
	const dataSelectedTimeElements = document.getElementsByClassName(selectedTime);
	if (dataSelectedTimeElements.length > 0) {
		for (const element of dataSelectedTimeElements) { element.value = flightTimes[selectedIndex]; }
	}
};

/**
	* Handles the change event of the slider.
	* @param {string[]} flightTimes - Array of flight times
	* @returns {void}
	*/
function handleSliderChange(flightTimes) {
	const selectedValue = flightTimes[this.value];
	const { validityRange, validityCb, selectedTime } = this.dataset;

	// Validate the selected value
	validateSelectedValue(selectedValue, validityRange, validityCb);

	// Update other elements with the selected time
	updateDisplayerElements(flightTimes, selectedTime, this.value);
};

// Initialize the flight times sliders by adding an input listener
(function () { addInputListenerToFlightTimesSliders(handleSliderChange); })();